/**
 * Console API access: one client, plus the interim staff identity switcher used
 * until the sai-staff Keycloak realm exists (doc 15 step 16).
 *
 * The switcher obtains its token from the API's quick-login endpoint, which is
 * refused in production. No staff credential is compiled into this bundle: a
 * fixed token in shipped JavaScript is a published credential, and the console
 * is served from a public origin.
 */
import { ServicesAiClient } from '@servicesai/api-client';
import { staffSsoCredential } from './staffSso.js';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8100';

export const api = new ServicesAiClient(API_URL);

/** Staff identities offered by the interim quick-login selector. */
export const QUICK_LOGIN_STAFF = [
  { id: 'maker', name: 'Noura Al Suwaidi', roles: 'reviewer · service editor' },
  { id: 'checker', name: 'Khalid Al Marri', roles: 'approver · admin' },
] as const;

export type QuickLoginStaffId = (typeof QUICK_LOGIN_STAFF)[number]['id'];

const TOKEN_KEY = 'sai.staffToken';
export const STAFF_TOKEN_CHANGED_EVENT = 'servicesai:staff-token-changed';

export function getStaffToken(): string {
  return staffSsoCredential() ?? localStorage.getItem(TOKEN_KEY) ?? '';
}

export function quickLoginStaffIdFromToken(token: string): QuickLoginStaffId | null {
  const [, staffId] = token.split('.');
  return QUICK_LOGIN_STAFF.some((staff) => staff.id === staffId)
    ? staffId as QuickLoginStaffId
    : null;
}

export async function issueQuickLoginToken(staffId: QuickLoginStaffId): Promise<string> {
  const response = await fetch(`${API_URL}/v1/admin/quick-login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ staffId }),
  });
  const body = await response.json().catch(() => ({})) as { token?: unknown };
  if (!response.ok || typeof body.token !== 'string' || !body.token) {
    throw new Error('quick login unavailable');
  }
  return body.token;
}

export function setStaffToken(token: string): void {
  const previousToken = getStaffToken();
  localStorage.setItem(TOKEN_KEY, token);
  window.dispatchEvent(
    new CustomEvent(STAFF_TOKEN_CHANGED_EVENT, { detail: { previousToken } }),
  );
}

/** Admin client bound to the currently selected staff identity. */
export function admin(): ReturnType<ServicesAiClient['admin']> {
  return api.admin(getStaffToken());
}
